import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  allowedRoles?: ('student' | 'landlord' | 'admin')[];
}

export default function ProtectedRoute({
  children,
  requireAuth = true,
  allowedRoles
}: ProtectedRouteProps) {
  // NOTE: This component provides client-side protection for better UX.
  // It is NOT a security barrier. Real data security is enforced by Supabase RLS on the backend.
  const { user, profile, loading, isAdmin, isHydrated } = useAuth();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isHydrated) setTimedOut(true);
    }, 10000);
    return () => clearTimeout(timeout);
  }, [isHydrated]);

  const authReady = isHydrated;

  if (!authReady && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (timedOut && !authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="text-xl font-semibold">Loading Timeout</h2>
          <p className="text-muted-foreground">
            The page is taking longer than expected to load. Please check your connection and try again.
          </p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Unverified users cannot access any protected route (dashboards, messages, etc.)
  if (requireAuth && user) {
    const emailConfirmed = (user as { email_confirmed_at?: string | null })?.email_confirmed_at;
    if (!emailConfirmed) {
      return <Navigate to="/auth" state={{ from: location, unverified: true }} replace />;
    }
  }

  // Logged-in user on a "guest" route (e.g. /auth): only redirect to dashboard if verified and profile is ready.
  // Otherwise show the page (e.g. Auth) to avoid redirect loop: dashboard would send unverified users back here.
  if (!requireAuth && user) {
    const emailConfirmed = (user as { email_confirmed_at?: string | null })?.email_confirmed_at;
    if (!emailConfirmed || !profile) {
      return <>{children}</>;
    }
    if (isAdmin) {
      return <Navigate to="/admin" replace />;
    }
    if (profile.user_type === 'landlord') {
      return <Navigate to="/landlord-dashboard" replace />;
    }
    // Students land on the Homepage — they can reach their dashboard via the navbar
    return <Navigate to="/" replace />;
  }

  // Role-based access: require profile when allowedRoles is set (unless admin)
  if (allowedRoles && allowedRoles.length > 0) {
    if (isAdmin) return <>{children}</>;
    if (!profile) {
      return <Navigate to="/auth" state={{ from: location }} replace />;
    }
    if (!allowedRoles.includes(profile.user_type)) {
      if (profile.user_type === 'landlord') {
        return <Navigate to="/landlord-dashboard" replace />;
      }
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}